import { preventFormSubmit } from '@helpers/@utils/prevent-form-submit';
import { yupResolver } from '@hookform/resolvers/yup';
import { set } from 'lodash';
import React, { useCallback, useImperativeHandle } from 'react';
import {
  FieldErrors,
  FieldValues,
  FormProvider,
  Path,
  SubmitHandler as RHFSubmitHandler,
  SubmitErrorHandler as RHFSubmitErrorHandler,
  useForm,
  useFormContext,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import TriggerFormInitially from './TriggerFormInitially';

export type SubmitHandler<
  TFieldValues extends FieldValues = FieldValues,
  TContext = AnyValue,
  TTransformedValues extends FieldValues | undefined = undefined
> = (
  data: TFieldValues,
  args: ExtendedUseFormReturn<TFieldValues, TContext, TTransformedValues>,
  event?: React.BaseSyntheticEvent
) => unknown | Promise<unknown>;

export type SubmitErrorHandler<
  TFieldValues extends FieldValues = FieldValues,
  TContext = AnyValue,
  TTransformedValues extends FieldValues | undefined = undefined
> = (
  data: FieldErrors<TFieldValues>,
  event?: React.BaseSyntheticEvent,
  args?: UseFormReturn<TFieldValues, TContext, TTransformedValues>
) => unknown | Promise<unknown>;

export type PartialSubmitHandler<
  TFieldValues extends FieldValues = FieldValues,
  TTransformedValues extends FieldValues | undefined = undefined
> = (
  fields: Path<TFieldValues>[],
  onValid?: TTransformedValues extends undefined
    ? RHFSubmitHandler<TFieldValues>
    : TTransformedValues extends FieldValues
    ? RHFSubmitHandler<TTransformedValues>
    : never,
  onInvalid?: RHFSubmitErrorHandler<TFieldValues>
) => (e?: React.BaseSyntheticEvent) => Promise<void>;

export interface FormHookWrapperProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = AnyValue,
  TTransformedValues extends FieldValues | undefined = undefined
> extends UseFormProps<TFieldValues, TContext> {
  validateInitially?: boolean;
  validationSchema?: AnyValue | (() => AnyValue);
  preventEnterSubmit?: boolean;
  validateAfterSubmitted?: boolean;
  onSubmit: SubmitHandler<TFieldValues, TContext, TTransformedValues>;
  onError?: SubmitErrorHandler<TFieldValues, TContext, TTransformedValues>;
  handleSubmit?: (
    e: React.BaseSyntheticEvent | undefined,
    args: ExtendedUseFormReturn<TFieldValues, TContext, TTransformedValues>
  ) => Promise<void>;
  children?: (args: ExtendedUseFormReturn<TFieldValues, TContext, TTransformedValues>) => React.ReactNode;
}

export type FormHookWrapperRef<
  TFieldValues extends FieldValues = FieldValues,
  TContext = AnyValue,
  TTransformedValues extends FieldValues | undefined = undefined
> = ExtendedUseFormReturn<TFieldValues, TContext, TTransformedValues> & {
  submit: () => Promise<void>;
};

function FormHookWrapperInner<
  TFieldValues extends FieldValues = FieldValues,
  TContext = AnyValue,
  TTransformedValues extends FieldValues | undefined = undefined
>(
  {
    children,
    onSubmit,
    onError,
    handleSubmit,
    validateInitially,
    validationSchema,
    preventEnterSubmit = true,
    validateAfterSubmitted = false,
    mode = 'onChange',
    ...props
  }: FormHookWrapperProps<TFieldValues, TContext, TTransformedValues>,
  ref: React.ForwardedRef<FormHookWrapperRef<TFieldValues, TContext, TTransformedValues>>
): JSX.Element {
  const args = useForm<TFieldValues, TContext, TTransformedValues>({
    mode,
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    ...props,
  }) as ExtendedUseFormReturn<TFieldValues, TContext, TTransformedValues>;

  args.displayErrorMessage = validateAfterSubmitted ? args.formState.isSubmitted : true;

  args.rerender = (values, options) => {
    args.reset(values, {
      keepValues: !values,
      keepIsSubmitted: true,
      keepDefaultValues: true,
      keepErrors: true,
      keepIsSubmitSuccessful: true,
      keepSubmitCount: true,
      keepDirty: true,
      keepTouched: true,
      ...options,
    });
    if (values) {
      args.trigger();
    }
  };

  args.setValues = (values, options) => {
    Object.keys(values).forEach((key) => {
      args.setValue(key as keyof TFieldValues as Path<TFieldValues>, values[key]);
    });
    if (options?.shouldValidate) {
      args.trigger();
    }
  };

  args.getErrors = async () => {
    let errors: FieldErrors<TFieldValues> = {};
    const handleSubmit = args.handleSubmit((() => undefined) as never, (errs) => (errors = errs));
    await handleSubmit();
    if (Object.keys(errors).length > 0) {
      console.log({ errors });
    }
    return errors;
  };

  const onFormSubmit: RHFSubmitHandler<TFieldValues> = useCallback(
    (data: TFieldValues, event?: React.BaseSyntheticEvent) => {
      return onSubmit(data, args, event) as Promise<void>;
    },
    [args, onSubmit]
  );

  const onFormError = useCallback(
    (data: FieldErrors<TFieldValues>, event?: React.BaseSyntheticEvent) => {
      return onError?.(data, event, args);
    },
    [args, onError]
  );

  args.partialSubmit = (fields, onValid, onInvalid) => async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const isValid = await args.trigger(fields);
    const data = args.getValues();

    if (isValid) {
      if (onValid) {
        await onValid(data, e);
      } else {
        await onFormSubmit(data, e);
      }
    } else {
      const partialErrors: FieldErrors<TFieldValues> = {};
      fields.forEach((field) => {
        const { error } = args.getFieldState(field, args.formState);
        if (error) {
          set(partialErrors, field, error);
        }
      });

      if (onInvalid) {
        await onInvalid(partialErrors, e);
      } else {
        await onFormError(partialErrors, e);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    ...args,
    submit: handleManualSubmit,
  }));

  const handleManualSubmit = (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    return handleSubmit ? handleSubmit(e, args) : args.handleSubmit(onFormSubmit as AnyValue, onFormError)();
  };

  return (
    <FormProvider {...args}>
      <>
        <form onKeyDown={preventEnterSubmit ? preventFormSubmit : undefined} onSubmit={handleManualSubmit}>
          {children?.(args)}
        </form>
        {validateInitially && <TriggerFormInitially<TFieldValues, TContext, TTransformedValues> {...args} />}
      </>
    </FormProvider>
  );
}

const FormHookWrapper = React.forwardRef(FormHookWrapperInner) as <
  TFieldValues extends FieldValues = FieldValues,
  TContext = AnyValue,
  TTransformedValues extends FieldValues | undefined = undefined
>(
  props: FormHookWrapperProps<TFieldValues, TContext, TTransformedValues> & {
    ref?: React.ForwardedRef<FormHookWrapperRef<TFieldValues, TContext, TTransformedValues>>;
  }
) => ReturnType<typeof FormHookWrapperInner<TFieldValues, TContext, TTransformedValues>>;

export interface ExtendedUseFormReturn<
  TFieldValues extends FieldValues = FieldValues,
  TContext = AnyValue,
  TTransformedValues extends FieldValues | undefined = undefined
> extends UseFormReturn<TFieldValues, TContext, TTransformedValues> {
  displayErrorMessage: boolean;
  rerender: UseFormReturn<TFieldValues, TContext, TTransformedValues>['reset'];
  getErrors: () => Promise<FieldErrors<TFieldValues>>;
  setValues: (values: TFieldValues, options?: { shouldValidate: boolean }) => void;
  partialSubmit: PartialSubmitHandler<TFieldValues, TTransformedValues>;
}

const useFormContextWithAdditionalProp = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = AnyValue,
  TTransformedValues extends FieldValues | undefined = undefined
>(): ExtendedUseFormReturn<TFieldValues, TContext, TTransformedValues> => {
  return useFormContext<TFieldValues, TContext, TTransformedValues>() as unknown as ExtendedUseFormReturn<
    TFieldValues,
    TContext,
    TTransformedValues
  >;
};

export { useFormContextWithAdditionalProp };

export default FormHookWrapper;
